import { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import { Card } from './core';

export type ChartData = {
  labels: string[];
  values: number[];
  label?: string;
  color?: string;
  colors?: string[];
  type?: 'bar' | 'line' | 'doughnut' | 'pie';
  yLabel?: string;
};

export const PALETTE = ['#6366f1', '#06b6d4', '#22c55e', '#f59e0b', '#ef4444', '#a855f7', '#14b8a6', '#ec4899'];

export function ChartCard({ title, data }: { title: string; data: ChartData | null }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (!data || !ref.current) return;
    const round = data.type === 'doughnut' || data.type === 'pie';
    const chart = new Chart(ref.current, {
      type: data.type ?? 'bar',
      data: {
        labels: data.labels,
        datasets: [{
          label: data.label,
          data: data.values,
          backgroundColor: data.colors ?? data.color ?? 'rgba(99,102,241,0.55)',
          borderColor: data.color ?? '#6366f1',
          borderWidth: round ? 0 : 2,
          tension: 0.3,
          fill: data.type === 'line',
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: round, position: 'bottom' as const } },
        scales: round
          ? undefined
          : {
              y: { beginAtZero: true, title: { display: !!data.yLabel, text: data.yLabel } },
              x: { ticks: { maxTicksLimit: 12 } },
            },
      },
    });
    return () => void chart.destroy();
  }, [data]);
  return (
    <Card title={title}>
      <div style={{ height: 260, position: 'relative' }}>
        {data ? <canvas ref={ref} /> : <p className="text-muted text-sm" style={{ margin: 0 }}>No data.</p>}
      </div>
    </Card>
  );
}

/**
 * Live-updating line chart. Unlike `ChartCard` the Chart instance is built once
 * and only its data is replaced, so a chart that refreshes every few seconds
 * scrolls instead of re-animating from zero on each poll.
 */
export function LiveLineChart(props: {
  title: string;
  labels: string[];
  values: number[];
  color: string;
  yLabel: string;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const chart = useRef<Chart | null>(null);
  useEffect(() => {
    if (!ref.current) return;
    chart.current = new Chart(ref.current, {
      type: 'line',
      data: { labels: [], datasets: [] },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        elements: { point: { radius: 0 } },
        plugins: { legend: { display: false } },
        scales: {
          y: { beginAtZero: true, title: { display: true, text: props.yLabel } },
          x: { ticks: { maxTicksLimit: 8 } },
        },
      },
    });
    return () => {
      chart.current?.destroy();
      chart.current = null;
    };
    // Built once: the update effect below owns the data.
  }, []);

  const { labels, values, color } = props;
  useEffect(() => {
    const c = chart.current;
    if (!c) return;
    c.data.labels = labels;
    c.data.datasets = [
      {
        label: props.title,
        data: values,
        borderColor: color,
        backgroundColor: `color-mix(in srgb, ${color} 15%, transparent)`,
        borderWidth: 2,
        fill: true,
        tension: 0.3,
      },
    ];
    c.update('none');
  });

  return (
    <Card title={props.title}>
      {/* The canvas stays mounted when empty: unmounting it would leave the
          build-once effect above with no element to attach to. */}
      <div style={{ height: 220, position: 'relative' }}>
        <canvas ref={ref} />
        {values.length === 0 ? (
          <p
            className="text-muted text-sm"
            style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', margin: 0 }}
          >
            No samples yet.
          </p>
        ) : null}
      </div>
    </Card>
  );
}

export interface SpectroDay {
  date: string;
  value: number;
  tooltip: string;
}

/**
 * Activity spectrogram — a GitHub-style contribution grid: one column per week,
 * one cell per day, opacity ramped by intensity. Day-of-week labels are
 * rendered by CSS counters so the caller only supplies the 30 days.
 */
export function Spectrogram({ days, label }: { days: SpectroDay[]; label: string }) {
  const max = Math.max(1, ...days.map((d) => d.value));
  // Pad the first week so column 0 always starts on Monday.
  const lead = days.length ? (new Date(days[0].date + 'T00:00:00Z').getUTCDay() + 6) % 7 : 0;
  return (
    <div className="spectro">
      <div className="spectro-days">
        {['Mon', '', 'Wed', '', 'Fri', '', 'Sun'].map((d, i) => <span key={i}>{d}</span>)}
      </div>
      <div className="spectro-grid" role="img" aria-label={label}>
        {Array.from({ length: lead }, (_, i) => <span key={`p${i}`} className="spectro-cell empty" />)}
        {days.map((d) => (
          <span
            key={d.date}
            className="spectro-cell"
            title={d.tooltip}
            style={d.value > 0
              ? { background: `color-mix(in srgb, var(--primary) ${25 + Math.round((d.value / max) * 75)}%, transparent)` }
              : undefined}
          />
        ))}
      </div>
      <div className="spectro-legend">
        <span className="text-muted text-xs">Less</span>
        {[0, 25, 50, 75, 100].map((p) => (
          <span
            key={p}
            className="spectro-cell"
            style={p === 0 ? undefined : { background: `color-mix(in srgb, var(--primary) ${p}%, transparent)` }}
          />
        ))}
        <span className="text-muted text-xs">More</span>
      </div>
    </div>
  );
}